import { useEffect, useRef, useState } from 'react';
import { DEFAULT_GAME_SOUND_CONFIG } from '@hypevault/shared-types';
import { ELEMENT_META, type HypeEggElement } from '@hypevault/games/hypeeggElements';
import { gameSounds } from '../lib/gameSounds';
import { Watermark } from './Watermark';

/**
 * HypeEgg overlay widget — octagonal cast-spawn popup.
 *
 * Modelled on the HypeFish redesign (Slots-style enter/show/exit). When
 * the engine spawns an egg, the octagon pops in and runs through the
 * three phases the engine writes:
 *   - `spawning` (≈0.8s): egg descends, lands with a splash
 *   - `awaiting`:        egg pulses, claim window counts down on a ring
 *   - `revealing`:       egg cracks → creature card rises with rarity frame
 * Then exits when the engine clears activeEgg.
 *
 * Sourced from /api/hypeegg/snapshot on mount + live updates via the
 * `game-state` socket filtered for `gameType === 'hypeegg'`.
 */

type HypeEggRarity = 'common' | 'rare' | 'epic' | 'legendary' | 'mythic';
type HypeEggPhase = 'spawning' | 'awaiting' | 'revealing';

interface ActiveEgg {
  id: string;
  rarity: HypeEggRarity;
  species: {
    id: string;
    name: string;
    emoji: string;
    flavor?: string;
    imageUrl?: string | null;
  };
  claimCost: number;
  spawnedAt: number;
  expiresAt: number;
  claimedBy: { viewer: string; isShiny?: boolean } | null;
  phase: HypeEggPhase;
  revealEndsAt: number | null;
}

interface BattleMove {
  i: number;
  attacker: 'a' | 'b';
  damage: number;
  defenderHp: number;
  ko: boolean;
  /** Pass 28i — defender's SPD made them slip the strike. */
  dodged?: boolean;
}
interface BattleSide {
  petId: string;
  viewer: string;
  speciesName: string;
  emoji: string;
  imageUrl?: string | null;
  rarity: HypeEggRarity;
  level: number;
  stats: { hp: number; attack: number; defense: number; speed: number };
  isShiny?: boolean;
  nickname?: string | null;
}
interface ActiveBattle {
  id: string;
  a: BattleSide;
  b: BattleSide;
  moves: BattleMove[];
  startedAt: number;
  currentMoveIndex: number;
  phase: 'intro' | 'fighting' | 'outcome';
  endsAt: number;
  winnerSide: 'a' | 'b';
  xpWinner: number;
  xpLoser: number;
  gemsAwarded: number;
  streakBonus: number;
  winnerLevelUp: number | null;
  isWild?: boolean;
}

interface ActivePetDisplay {
  viewer: string;
  petId: string;
  speciesId: string;
  speciesName: string;
  rarity: HypeEggRarity;
  emoji: string;
  imageUrl?: string | null;
  level: number;
  xp: number;
  wins: number;
  losses: number;
  isShiny?: boolean;
  nickname?: string | null;
  currentHp?: number;
  maxHp?: number;
  isFainted?: boolean;
  endsAt: number;
}

interface TournamentState {
  startedAt: number;
  endsAt: number;
  rewardMultiplier: number;
  battleCooldownSec: number;
  label?: string;
}

interface SpawnBoostState {
  startedAt: number;
  endsAt: number;
  intervalMultiplier: number;
  rarityWeightMultiplier: number;
}

interface WildParticipant {
  viewer: string;
  damageDealt: number;
  catchAttempted?: boolean;
  caught?: boolean;
}

interface ActiveWild {
  id: string;
  rarity: 'uncommon' | 'common' | 'rare' | 'epic' | 'legendary' | 'mythic';
  species: {
    id: string;
    name: string;
    emoji: string;
    imageUrl?: string | null;
    /** Pass 24 — surface elemental type(s) so the overlay can show
     *  viewers which counter to switch to before they !attack. */
    element?: string | null;
    secondaryElement?: string | null;
  };
  level: number;
  maxHp: number;
  currentHp: number;
  status: 'attacking' | 'catch_phase' | 'concluded';
  spawnedAt: number;
  escapeAt: number;
  catchPhaseEndsAt: number | null;
  participants: WildParticipant[];
  outcome?: 'escaped' | 'no_catchers' | 'all_attempted' | 'time_up';
  /** Pass 26b — runner-pushed FX entries for the attack overlay.
   *  Engine prunes after WILD_FX_TTL_MS. Overlay dedups by id. */
  recentAttackFx?: WildFxEntry[];
}

interface WildFxEntry {
  id: string;
  attackerViewer: string;
  attackId: string;
  attackName: string;
  attackEmoji: string;
  element: string;
  outcome: 'hit' | 'miss' | 'dodge';
  damage: number;
  effectiveness?: 'super' | 'normal' | 'resisted';
  ts: number;
}

interface HypeEggState {
  activeEgg?: ActiveEgg | null;
  activeWild?: ActiveWild | null;
  activeBattle?: ActiveBattle | null;
  activePetDisplay?: ActivePetDisplay | null;
  tournament?: TournamentState | null;
  spawnBoost?: SpawnBoostState | null;
}

interface GameStateEvent {
  gameType: string;
  state: unknown;
  config: unknown;
  isActive: boolean;
}

interface HypeEggSnapshot {
  state: HypeEggState | null;
  config: unknown;
  isActive: boolean;
  /** Phase 19f — widget.fields blob. Carries per-streamer overrides
   *  for the four wild-encounter sound URLs. */
  widgetFields?: {
    sounds?: {
      wild_spawn?: string;
      wild_attack?: string;
      wild_miss?: string;
      wild_heal?: string;
      wild_catch_success?: string;
      wild_catch_fail?: string;
    } | null;
  } | null;
}

const RARITY: Record<
  HypeEggRarity,
  { label: string; frame: string; glow: string; gradient: string; mythicPrism: boolean }
> = {
  common: {
    label: 'Common',
    frame: '#94a3b8',
    glow: 'rgba(148,163,184,0.55)',
    gradient: 'linear-gradient(160deg, #475569 0%, #1e293b 100%)',
    mythicPrism: false,
  },
  rare: {
    label: 'Rare',
    frame: '#60a5fa',
    glow: 'rgba(96,165,250,0.7)',
    gradient: 'linear-gradient(160deg, #1d4ed8 0%, #0c2e6e 100%)',
    mythicPrism: false,
  },
  epic: {
    label: 'Epic',
    frame: '#c084fc',
    glow: 'rgba(192,132,252,0.8)',
    gradient: 'linear-gradient(160deg, #7c3aed 0%, #3b1078 100%)',
    mythicPrism: false,
  },
  legendary: {
    label: 'Legendary',
    frame: '#fbbf24',
    glow: 'rgba(251,191,36,0.9)',
    gradient: 'linear-gradient(160deg, #f59e0b 0%, #b45309 60%, #7c2d12 100%)',
    mythicPrism: false,
  },
  mythic: {
    label: 'Mythic',
    frame: '#fb7185',
    glow: 'rgba(251,113,133,1)',
    gradient:
      'linear-gradient(135deg, #f97316 0%, #ec4899 25%, #a855f7 55%, #06b6d4 85%, #fde047 100%)',
    mythicPrism: true,
  },
};

const ANIMATIONS = `
@keyframes he-popIn { 0% { transform: translate(-50%, -50%) scale(0.6) rotate(-8deg); opacity: 0 } 65% { transform: translate(-50%, -50%) scale(1.06) rotate(2deg); opacity: 1 } 100% { transform: translate(-50%, -50%) scale(1) rotate(0); opacity: 1 } }
@keyframes wildSlideIn { 0% { transform: translate(-50%, -40%) scale(0.85); opacity: 0 } 100% { transform: translate(-50%, -50%) scale(1); opacity: 1 } }
@keyframes he-popOut { 0% { transform: translate(-50%, -50%) scale(1); opacity: 1 } 100% { transform: translate(-50%, -50%) scale(0.7); opacity: 0 } }
@keyframes he-octaGlow { 0%, 100% { filter: drop-shadow(0 0 24px var(--rarity-glow)) drop-shadow(0 0 8px var(--rarity-glow)) } 50% { filter: drop-shadow(0 0 48px var(--rarity-glow)) drop-shadow(0 0 14px var(--rarity-glow)) } }
@keyframes he-octaPrism { from { background-position: 0% 50% } to { background-position: 200% 50% } }
@keyframes he-eggDrop { 0% { transform: translate(-50%, -240%) rotate(-12deg); opacity: 0 } 70% { transform: translate(-50%, 8%) rotate(4deg); opacity: 1 } 100% { transform: translate(-50%, 0%) rotate(0deg); opacity: 1 } }
@keyframes he-eggBob { 0%, 100% { transform: translate(-50%, 0%) rotate(-3deg) } 50% { transform: translate(-50%, -8%) rotate(3deg) } }
@keyframes he-eggCrack { 0% { transform: translate(-50%, 0%) scale(1); opacity: 1 } 60% { transform: translate(-50%, 0%) scale(1.1); opacity: 1 } 100% { transform: translate(-50%, 0%) scale(0.6); opacity: 0 } }
@keyframes he-splash { 0% { transform: translate(-50%, -50%) scale(0); opacity: 1 } 100% { transform: translate(-50%, -50%) scale(2.4); opacity: 0 } }
@keyframes he-cardRise { 0% { transform: translate(-50%, 60%) scale(0.5); opacity: 0 } 60% { transform: translate(-50%, -52%) scale(1.05); opacity: 1 } 100% { transform: translate(-50%, -50%) scale(1); opacity: 1 } }
@keyframes he-sparkle { 0%, 100% { opacity: 0; transform: scale(0.6) } 40% { opacity: 1; transform: scale(1.2) } }
@keyframes he-ringDrain { from { stroke-dashoffset: 0 } to { stroke-dashoffset: 360 } }
`;

const ENTER_MS = 500;
const EXIT_MS = 360;
const OCTA_SIZE = 560;
const OCTAGON_CLIP =
  'polygon(29.3% 0%, 70.7% 0%, 100% 29.3%, 100% 70.7%, 70.7% 100%, 29.3% 100%, 0% 70.7%, 0% 29.3%)';

export function HypeEggWidget({
  gameStateEvent,
  streamerTier = 'free',
}: {
  gameStateEvent: GameStateEvent | null;
  streamerTier?: 'free' | 'paid';
}) {
  const [snapshot, setSnapshot] = useState<HypeEggSnapshot | null>(null);
  const bootstrappedRef = useRef(false);

  // Configure the shared sound manager once on mount. Without this the
  // volume stays at 0 and every gameSounds.play() call early-returns
  // silently — that's why pre-Pass-13 sound calls in this widget were
  // no-ops. Future: pull per-streamer overrides from the snapshot.
  useEffect(() => {
    gameSounds.configure(DEFAULT_GAME_SOUND_CONFIG);
    // Eagerly create + resume the AudioContext on first user
    // interaction. OBS CEF allows autoplay so this rarely matters
    // there, but anyone testing the overlay URL directly in Chrome
    // needs to have clicked the page once before the browser will
    // permit playback. The once:true listener auto-removes after
    // first trigger.
    function unlock() {
      void gameSounds.unlock();
    }
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  useEffect(() => {
    if (bootstrappedRef.current) return;
    const tokenMatch = window.location.search.match(/token=([^&]+)/);
    if (!tokenMatch) return;
    bootstrappedRef.current = true;
    const ac = new AbortController();
    fetch(`/api/hypeegg/snapshot?token=${tokenMatch[1]}`, { signal: ac.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then((json: HypeEggSnapshot | null) => {
        if (json) setSnapshot(json);
      })
      .catch(() => undefined);
    return () => ac.abort();
  }, []);

  // Phase 19f — apply per-streamer sound URL overrides from the
  // snapshot. Re-runs whenever widget fields change (streamer edits
  // a URL in the dashboard → /api/widget-configs PATCH → next snapshot
  // → registerPackAsset overwrites the default).
  useEffect(() => {
    const sounds = snapshot?.widgetFields?.sounds;
    if (!sounds) return;
    if (sounds.wild_spawn) {
      gameSounds.registerPackAsset('default', 'wild_spawn', sounds.wild_spawn);
    }
    if (sounds.wild_attack) {
      gameSounds.registerPackAsset('default', 'wild_attack', sounds.wild_attack);
    }
    if (sounds.wild_miss) {
      gameSounds.registerPackAsset('default', 'wild_miss', sounds.wild_miss);
    }
    if (sounds.wild_heal) {
      gameSounds.registerPackAsset('default', 'wild_heal', sounds.wild_heal);
    }
    if (sounds.wild_catch_success) {
      gameSounds.registerPackAsset('default', 'wild_catch_success', sounds.wild_catch_success);
    }
    if (sounds.wild_catch_fail) {
      gameSounds.registerPackAsset('default', 'wild_catch_fail', sounds.wild_catch_fail);
    }
  }, [snapshot?.widgetFields]);

  // Phase 19f — sound triggers tied to activeWild transitions:
  //  - new wild id appears             → wild_spawn
  //  - currentHp drops between updates → wild_attack
  //  - participants[].caught flips     → wild_catch_success
  //  - participants[].catchAttempted   → wild_catch_fail (when not caught)
  // Refs hold the previous snapshot's values so we only fire on edges,
  // not on every re-render.
  const prevWildIdRef = useRef<string | null>(null);
  const prevWildHpRef = useRef<number | null>(null);
  const prevCaughtRef = useRef<Set<string>>(new Set());
  const prevAttemptedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const wild = snapshot?.state?.activeWild ?? null;
    const prevId = prevWildIdRef.current;
    const prevHp = prevWildHpRef.current;
    const prevCaught = prevCaughtRef.current;
    const prevAttempted = prevAttemptedRef.current;
    if (!wild) {
      prevWildIdRef.current = null;
      prevWildHpRef.current = null;
      prevCaughtRef.current = new Set();
      prevAttemptedRef.current = new Set();
      return;
    }
    if (wild.id !== prevId) {
      void gameSounds.play('wild_spawn');
      prevCaughtRef.current = new Set();
      prevAttemptedRef.current = new Set();
    } else if (prevHp != null && wild.currentHp < prevHp) {
      void gameSounds.play('wild_attack');
    }
    prevWildIdRef.current = wild.id;
    prevWildHpRef.current = wild.currentHp;
    // Catch outcomes — fire one sound per participant edge.
    for (const p of wild.participants ?? []) {
      const key = `${p.viewer}`;
      if (p.caught && !prevCaught.has(key)) {
        void gameSounds.play('wild_catch_success');
        prevCaught.add(key);
      } else if (p.catchAttempted && !p.caught && !prevAttempted.has(key)) {
        void gameSounds.play('wild_catch_fail');
        prevAttempted.add(key);
      }
    }
  }, [snapshot?.state?.activeWild]);

  useEffect(() => {
    if (!gameStateEvent || gameStateEvent.gameType !== 'hypeegg') return;
    // Phase 19f — preserve widgetFields across live state updates;
    // the game-state socket event only carries the engine state row,
    // not the widget_configs.fields blob (which arrives once via the
    // bootstrap snapshot).
    setSnapshot((prev) => ({
      state: gameStateEvent.state as HypeEggState | null,
      config: gameStateEvent.config,
      isActive: gameStateEvent.isActive,
      widgetFields: prev?.widgetFields ?? null,
    }));
  }, [gameStateEvent]);

  const isActive = snapshot?.isActive ?? false;
  const activeEgg = isActive ? (snapshot?.state?.activeEgg ?? null) : null;
  const activeWild = isActive ? (snapshot?.state?.activeWild ?? null) : null;
  const activeBattle = isActive ? (snapshot?.state?.activeBattle ?? null) : null;
  const activePetDisplay = isActive ? (snapshot?.state?.activePetDisplay ?? null) : null;
  const tournament = isActive ? (snapshot?.state?.tournament ?? null) : null;
  const spawnBoost = isActive ? (snapshot?.state?.spawnBoost ?? null) : null;

  return (
    <>
      <style>{ANIMATIONS}</style>
      <EventBanner tournament={tournament} spawnBoost={spawnBoost} />
      {/* Priority: battle > !pet display > wild encounter > legacy egg.
          Only one octagon on screen at a time. */}
      {activeBattle ? (
        <BattleArena battle={activeBattle} streamerTier={streamerTier} />
      ) : activePetDisplay ? (
        <PetDisplayOctagon display={activePetDisplay} streamerTier={streamerTier} />
      ) : activeWild ? (
        <>
          <WildOctagon wild={activeWild} streamerTier={streamerTier} />
          <WildAttackFxLayer recentFx={activeWild.recentAttackFx ?? []} />
        </>
      ) : (
        <EggOctagon egg={activeEgg} streamerTier={streamerTier} />
      )}
    </>
  );
}

/** Pass 26b — element-themed FX overlay for wild-encounter attacks.
 *  Reads the engine's recentAttackFx ring buffer, dedupes by id, and
 *  plays a 1.2s animation per new entry. Misses spawn a floating
 *  "MISS" tag; dodges spawn "DODGE". Hits trigger element-specific
 *  fullscreen visuals (snow + shake for ice, embers for fire, etc.).
 *
 *  All effects are pointerEvents:none so they don't intercept clicks
 *  on chat — important since OBS captures the browser layer above
 *  the stream feed and any click-through would be a regression. */
function WildAttackFxLayer({ recentFx }: { recentFx: WildFxEntry[] }) {
  // Refs (not state) so adding a new entry to the seen set doesn't
  // retrigger the effect. Earlier version put `animatedIds` in the dep
  // array; the resulting cleanup torn down the just-scheduled
  // setTimeouts before they could clear `active`, leaving glitch /
  // poison element overlays painted on the widget forever.
  const animatedIdsRef = useRef<Set<string>>(new Set());
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const [active, setActive] = useState<WildFxEntry[]>([]);

  useEffect(() => {
    const fresh = recentFx.filter((fx) => !animatedIdsRef.current.has(fx.id));
    if (fresh.length === 0) return;
    for (const fx of fresh) animatedIdsRef.current.add(fx.id);
    setActive((prev) => [...prev, ...fresh]);
    for (const fx of fresh) {
      const t = setTimeout(() => {
        setActive((prev) => prev.filter((e) => e.id !== fx.id));
      }, 1200);
      timersRef.current.push(t);
    }
  }, [recentFx]);

  // Single unmount cleanup. Don't clear timers between recentFx
  // updates — that's what caused the lingering overlay bug.
  useEffect(() => {
    return () => {
      for (const t of timersRef.current) clearTimeout(t);
      timersRef.current = [];
    };
  }, []);

  if (active.length === 0) return null;
  return (
    <>
      <style>{WILD_FX_KEYFRAMES}</style>
      {active.map((fx) => (
        <WildAttackFx key={fx.id} fx={fx} />
      ))}
    </>
  );
}

const WILD_FX_KEYFRAMES = `
@keyframes wildFxShake {
  0%, 100% { transform: translate(-50%, -50%); }
  10%, 30%, 50%, 70%, 90% { transform: translate(calc(-50% - 8px), calc(-50% - 4px)); }
  20%, 40%, 60%, 80% { transform: translate(calc(-50% + 8px), calc(-50% + 4px)); }
}
@keyframes wildFxShakeHard {
  0%, 100% { transform: translate(-50%, -50%); }
  10%, 30%, 50%, 70%, 90% { transform: translate(calc(-50% - 14px), calc(-50% - 8px)); }
  20%, 40%, 60%, 80% { transform: translate(calc(-50% + 14px), calc(-50% + 8px)); }
}
@keyframes wildFxFlash {
  0% { opacity: 0; }
  10% { opacity: 0.85; }
  60% { opacity: 0.3; }
  100% { opacity: 0; }
}
@keyframes wildFxFallDown {
  0% { transform: translateY(-100vh) rotate(0deg); opacity: 0; }
  10% { opacity: 1; }
  100% { transform: translateY(100vh) rotate(360deg); opacity: 0; }
}
@keyframes wildFxRiseUp {
  0% { transform: translateY(40vh) scale(0.6); opacity: 0; }
  20% { opacity: 1; }
  100% { transform: translateY(-30vh) scale(1.2); opacity: 0; }
}
@keyframes wildFxRipple {
  0% { transform: translate(-50%, -50%) scale(0.2); opacity: 0.8; }
  100% { transform: translate(-50%, -50%) scale(3.2); opacity: 0; }
}
@keyframes wildFxSwirl {
  0% { transform: rotate(0deg) scale(0.4); opacity: 0; }
  20% { opacity: 1; }
  100% { transform: rotate(540deg) scale(1.5); opacity: 0; }
}
@keyframes wildFxStreak {
  0% { transform: translateX(-100vw); opacity: 0; }
  20% { opacity: 1; }
  100% { transform: translateX(100vw); opacity: 0; }
}
@keyframes wildFxBlinkMiss {
  0% { transform: translate(-50%, 0) scale(0.5); opacity: 0; }
  20% { transform: translate(-50%, -20px) scale(1.05); opacity: 1; }
  80% { transform: translate(-50%, -60px) scale(1); opacity: 1; }
  100% { transform: translate(-50%, -100px) scale(1); opacity: 0; }
}
@keyframes wildFxStrobe {
  0%, 100% { opacity: 0; }
  10%, 30%, 50%, 70%, 90% { opacity: 0.9; }
  20%, 40%, 60%, 80% { opacity: 0.2; }
}
@keyframes wildFxGlitchSlide {
  0% { transform: translateX(0); }
  20% { transform: translateX(-6px); }
  40% { transform: translateX(8px); }
  60% { transform: translateX(-4px); }
  80% { transform: translateX(5px); }
  100% { transform: translateX(0); }
}
`;

const ELEMENT_FX: Record<
  string,
  {
    /** Background fullscreen color overlay. */
    flash: string;
    /** Whether the wild octagon should shake. */
    shake: 'none' | 'shake' | 'shakeHard';
    /** Particle emojis to fall / rise. */
    particles: string[];
    /** Particle direction. */
    particleDir: 'fall' | 'rise' | 'streak' | 'swirl' | 'ripple' | 'none';
  }
> = {
  fire: {
    flash: 'rgba(249,115,22,0.35)',
    shake: 'shake',
    particles: ['🔥', '🔥', '✨'],
    particleDir: 'rise',
  },
  water: {
    flash: 'rgba(56,189,248,0.35)',
    shake: 'none',
    particles: ['💧', '💦', '💧'],
    particleDir: 'ripple',
  },
  grass: {
    flash: 'rgba(34,197,94,0.30)',
    shake: 'shake',
    particles: ['🌿', '🍃', '🌱'],
    particleDir: 'swirl',
  },
  earth: {
    flash: 'rgba(120,72,30,0.35)',
    shake: 'shakeHard',
    particles: ['🪨', '🪨', '💢'],
    particleDir: 'fall',
  },
  electric: {
    flash: 'rgba(250,204,21,0.55)',
    shake: 'shake',
    particles: ['⚡', '⚡', '🌩️'],
    particleDir: 'streak',
  },
  ice: {
    flash: 'rgba(186,230,253,0.30)',
    shake: 'shake',
    particles: ['❄️', '❄️', '🌨️'],
    particleDir: 'fall',
  },
  wind: {
    flash: 'rgba(226,232,240,0.25)',
    shake: 'none',
    particles: ['💨', '🌪️', '💨'],
    particleDir: 'streak',
  },
  shadow: {
    flash: 'rgba(15,15,30,0.55)',
    shake: 'shake',
    particles: ['🌑', '🕳️', '⬛'],
    particleDir: 'swirl',
  },
  light: {
    flash: 'rgba(255,255,255,0.55)',
    shake: 'none',
    particles: ['✨', '☀️', '💡'],
    particleDir: 'ripple',
  },
  spirit: {
    flash: 'rgba(167,139,250,0.40)',
    shake: 'shake',
    particles: ['👻', '💜', '🔮'],
    particleDir: 'rise',
  },
  hype: {
    flash: 'rgba(244,114,182,0.40)',
    shake: 'shakeHard',
    particles: ['🤘', '💥', '🎉'],
    particleDir: 'rise',
  },
  chaos: {
    flash: 'rgba(217,70,239,0.40)',
    shake: 'shakeHard',
    particles: ['🌀', '🔀', '🎲'],
    particleDir: 'swirl',
  },
  glitch: {
    flash: 'rgba(168,85,247,0.40)',
    shake: 'shake',
    particles: ['📺', '🟪', '💥'],
    particleDir: 'streak',
  },
};

function WildAttackFx({ fx }: { fx: WildFxEntry }) {
  const meta = ELEMENT_FX[fx.element] ?? ELEMENT_FX.hype!;
  const isHit = fx.outcome === 'hit';
  const isMiss = fx.outcome === 'miss';
  const isDodge = fx.outcome === 'dodge';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 1100,
        overflow: 'hidden',
      }}
    >
      {/* Element flash */}
      {isHit && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: meta.flash,
            mixBlendMode: fx.element === 'shadow' ? 'multiply' : 'screen',
            animation: 'wildFxFlash 1.2s ease-out forwards',
          }}
        />
      )}

      {/* Electric strobe overlay — stacks on top of the flash for that
          unmistakable "lightning is happening" feel. */}
      {isHit && fx.element === 'electric' && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(255,255,255,0.6)',
            animation: 'wildFxStrobe 1.2s ease-out forwards',
          }}
        />
      )}

      {/* Glitch slide bars — three thin horizontal bars sliding x */}
      {isHit && fx.element === 'glitch' && (
        <>
          {[20, 50, 78].map((top, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: 0,
                top: `${top}%`,
                width: '100%',
                height: 18,
                background: 'rgba(168,85,247,0.55)',
                mixBlendMode: 'screen',
                animation: `wildFxGlitchSlide ${0.18 + i * 0.05}s linear infinite`,
              }}
            />
          ))}
        </>
      )}

      {/* Particle layer */}
      {isHit && meta.particles.length > 0 && (
        <WildFxParticles
          particles={meta.particles}
          dir={meta.particleDir}
          count={fx.element === 'ice' ? 28 : 14}
        />
      )}

      {/* Wild octagon shake — re-mounts the existing octagon via a
          shake-class wrapper. The OG octagon is already centered; we
          paint a transparent shake rectangle sized to roughly the
          octagon footprint so its motion reads as "the wild got hit"
          rather than the FX layer moving. */}
      {isHit && meta.shake !== 'none' && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: 720,
            height: 720,
            border: '4px solid transparent',
            animation: `wildFx${meta.shake === 'shakeHard' ? 'ShakeHard' : 'Shake'} 0.45s linear`,
          }}
        />
      )}

      {/* Outcome stamp — MISS / DODGE / damage centered over the wild */}
      <div
        style={{
          position: 'absolute',
          top: '38%',
          left: '50%',
          color: isMiss
            ? '#f97316'
            : isDodge
              ? '#38bdf8'
              : fx.effectiveness === 'super'
                ? '#fbbf24'
                : fx.effectiveness === 'resisted'
                  ? '#94a3b8'
                  : '#f87171',
          fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
          fontWeight: 900,
          fontSize: isHit ? 72 : 56,
          letterSpacing: 2,
          textShadow:
            '0 4px 8px rgba(0,0,0,0.9), 0 0 32px ' +
            (isMiss
              ? 'rgba(249,115,22,0.6)'
              : isDodge
                ? 'rgba(56,189,248,0.6)'
                : 'rgba(0,0,0,0.6)'),
          animation: 'wildFxBlinkMiss 1.2s ease-out forwards',
          pointerEvents: 'none',
        }}
      >
        {isMiss
          ? `${fx.attackEmoji} MISS`
          : isDodge
            ? `💨 DODGE`
            : `${fx.attackEmoji} ${fx.damage}${fx.effectiveness === 'super' ? ' ✨' : ''}`}
      </div>
    </div>
  );
}

function WildFxParticles({
  particles,
  dir,
  count,
}: {
  particles: string[];
  dir: 'fall' | 'rise' | 'streak' | 'swirl' | 'ripple' | 'none';
  count: number;
}) {
  if (dir === 'none') return null;
  const items = Array.from({ length: count }, (_, i) => i);
  return (
    <>
      {items.map((i) => {
        const p = particles[i % particles.length]!;
        const left = Math.random() * 100;
        const top = Math.random() * 100;
        const delay = Math.random() * 0.25;
        const dur = 0.9 + Math.random() * 0.45;
        const size = 24 + Math.random() * 28;
        let animation = '';
        let posStyle: React.CSSProperties = {};
        if (dir === 'fall') {
          animation = `wildFxFallDown ${dur}s linear ${delay}s forwards`;
          posStyle = { left: `${left}%`, top: 0 };
        } else if (dir === 'rise') {
          animation = `wildFxRiseUp ${dur}s ease-out ${delay}s forwards`;
          posStyle = { left: `${left}%`, bottom: 0 };
        } else if (dir === 'streak') {
          animation = `wildFxStreak ${dur}s linear ${delay}s forwards`;
          posStyle = { left: 0, top: `${top}%` };
        } else if (dir === 'ripple') {
          animation = `wildFxRipple ${dur}s ease-out ${delay}s forwards`;
          posStyle = { left: '50%', top: '50%' };
        } else if (dir === 'swirl') {
          animation = `wildFxSwirl ${dur}s ease-in-out ${delay}s forwards`;
          posStyle = { left: `${left}%`, top: `${top}%` };
        }
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              fontSize: size,
              ...posStyle,
              animation,
              pointerEvents: 'none',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))',
            }}
          >
            {p}
          </div>
        );
      })}
    </>
  );
}

/** Phase 19e — wild-encounter overlay. Shows the species art in an
 *  octagon frame with a level/name label, an HP bar, a status caption
 *  (Attacking / Catch Phase X seconds), and a participant counter.
 *  Re-renders once a second to keep the countdown live. */
function WildOctagon({ wild, streamerTier }: { wild: ActiveWild; streamerTier: 'free' | 'paid' }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const hpPct = wild.maxHp > 0 ? Math.max(0, (wild.currentHp / wild.maxHp) * 100) : 0;
  const hpBarColor = hpPct > 60 ? '#34d399' : hpPct > 25 ? '#facc15' : '#f87171';
  const isCatchPhase = wild.status === 'catch_phase';
  const isConcluded = wild.status === 'concluded';
  const remainingMs = isCatchPhase
    ? Math.max(0, (wild.catchPhaseEndsAt ?? 0) - now)
    : Math.max(0, wild.escapeAt - now);
  const remainingSec = Math.ceil(remainingMs / 1000);
  const minutes = Math.floor(remainingSec / 60);
  const seconds = remainingSec % 60;
  const timeStr = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;

  const RARITY_GLOW: Record<string, string> = {
    common: 'rgba(161,161,170,0.7)',
    uncommon: 'rgba(52,211,153,0.75)',
    rare: 'rgba(56,189,248,0.8)',
    epic: 'rgba(192,132,252,0.9)',
    legendary: 'rgba(251,191,36,1)',
    mythic: 'rgba(244,114,182,1)',
  };
  const glow = RARITY_GLOW[wild.rarity] ?? RARITY_GLOW.uncommon;

  return (
    <div
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1000,
        textAlign: 'center',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        color: '#fff',
        pointerEvents: 'none',
        animation: 'wildSlideIn 600ms cubic-bezier(0.22, 1, 0.36, 1)',
      }}
    >
      {/* Status banner above */}
      <div
        style={{
          padding: '18px 48px',
          borderRadius: 999,
          background: isCatchPhase
            ? 'linear-gradient(90deg, rgba(34,197,94,0.5), rgba(34,197,94,0.2))'
            : isConcluded
              ? 'rgba(0,0,0,0.6)'
              : 'rgba(0,0,0,0.6)',
          display: 'inline-block',
          marginBottom: 42,
          fontSize: 38,
          fontWeight: 700,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
          textShadow: '0 3px 6px rgba(0,0,0,0.8)',
        }}
      >
        {isCatchPhase
          ? `🎯 CATCH PHASE — ${timeStr} · type !catch`
          : isConcluded
            ? wild.outcome === 'escaped'
              ? '💨 ESCAPED'
              : '✅ ENCOUNTER OVER'
            : `⚔️ WILD ENCOUNTER — escapes in ${timeStr}`}
      </div>

      {/* Octagonal art frame */}
      <div
        style={{
          width: 960,
          height: 960,
          margin: '0 auto',
          clipPath: 'polygon(30% 0, 70% 0, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0 70%, 0 30%)',
          background: 'rgba(0,0,0,0.65)',
          display: 'grid',
          placeItems: 'center',
          boxShadow: `0 0 120px 24px ${glow}`,
          position: 'relative',
        }}
      >
        {wild.species.imageUrl ? (
          // eslint-disable-next-line jsx-a11y/img-redundant-alt, @next/next/no-img-element
          <img
            src={wild.species.imageUrl}
            alt=""
            style={{
              width: '92%',
              height: '92%',
              objectFit: 'cover',
              clipPath: 'inherit',
            }}
          />
        ) : (
          <span style={{ fontSize: 432, filter: 'drop-shadow(0 12px 36px rgba(0,0,0,0.7))' }}>
            {wild.species.emoji}
          </span>
        )}
        {streamerTier === 'free' && <Watermark />}
      </div>

      {/* Identity label */}
      <div
        style={{
          marginTop: 48,
          fontFamily: '"Bebas Neue", "Segoe UI", system-ui, sans-serif',
          fontSize: 84,
          fontWeight: 900,
          letterSpacing: 6,
          textShadow: '0 6px 18px rgba(0,0,0,0.9)',
        }}
      >
        Lv. {wild.level} {wild.species.name.toUpperCase()}
      </div>

      {/* Type badges — tells viewers which counter to swap to before
          they !attack. Dual-typed wilds show both. */}
      <WildTypeBadges primary={wild.species.element} secondary={wild.species.secondaryElement} />

      {/* HP bar */}
      <div
        style={{
          width: 960,
          margin: '36px auto 0',
          padding: 12,
          background: 'rgba(0,0,0,0.7)',
          borderRadius: 24,
        }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: 54,
            background: 'rgba(0,0,0,0.6)',
            borderRadius: 12,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${hpPct}%`,
              height: '100%',
              background: `linear-gradient(90deg, ${hpBarColor}, ${hpBarColor}cc)`,
              transition: 'width 320ms ease-out',
              boxShadow: `inset 0 0 24px ${hpBarColor}`,
            }}
          />
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              fontSize: 38,
              fontWeight: 700,
              letterSpacing: 3,
              textShadow: '0 3px 6px rgba(0,0,0,1)',
            }}
          >
            HP {wild.currentHp} / {wild.maxHp}
          </div>
        </div>
      </div>

      {/* Participants */}
      {wild.participants.length > 0 && (
        <div
          style={{
            marginTop: 24,
            fontSize: 36,
            opacity: 0.85,
            textShadow: '0 3px 6px rgba(0,0,0,0.9)',
          }}
        >
          ⚔️ {wild.participants.length} attacker{wild.participants.length === 1 ? '' : 's'} ·{' '}
          {wild.participants
            .slice(0, 5)
            .map((p) => p.viewer)
            .join(', ')}
          {wild.participants.length > 5 ? ` +${wild.participants.length - 5}` : ''}
        </div>
      )}
    </div>
  );
}

/** Pass 24 — show the wild's elemental type(s) above the HP bar so
 *  viewers know which counter to swap to before they !attack. Dual-
 *  typed wilds render both badges side by side. Falls back to a
 *  neutral chip when the species predates the Pass 19 type system. */
function WildTypeBadges({
  primary,
  secondary,
}: {
  primary?: string | null | undefined;
  secondary?: string | null | undefined;
}) {
  const items = [primary, secondary]
    .map((e) => (e && e in ELEMENT_META ? (e as HypeEggElement) : null))
    .filter((e): e is HypeEggElement => e != null);
  if (items.length === 0) {
    return (
      <div
        style={{
          marginTop: 18,
          display: 'inline-block',
          padding: '8px 18px',
          borderRadius: 999,
          background: 'rgba(0,0,0,0.55)',
          border: '2px solid rgba(255,255,255,0.25)',
          color: 'rgba(255,255,255,0.7)',
          fontFamily: '"Bebas Neue", "Segoe UI", system-ui, sans-serif',
          fontSize: 28,
          letterSpacing: 4,
          textShadow: '0 2px 6px rgba(0,0,0,0.8)',
        }}
      >
        Type ?
      </div>
    );
  }
  return (
    <div
      style={{
        marginTop: 18,
        display: 'flex',
        justifyContent: 'center',
        gap: 14,
      }}
    >
      {items.map((el, i) => {
        const meta = ELEMENT_META[el];
        return (
          <span
            key={`${el}-${i}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 22px',
              borderRadius: 999,
              background: meta.bg,
              border: `2px solid ${meta.color}`,
              boxShadow: `0 0 24px ${meta.color}66, inset 0 0 16px ${meta.color}33`,
              color: '#fff',
              fontFamily: '"Bebas Neue", "Segoe UI", system-ui, sans-serif',
              fontSize: 32,
              fontWeight: 700,
              letterSpacing: 4,
              textTransform: 'uppercase',
              textShadow: `0 2px 6px rgba(0,0,0,0.85), 0 0 14px ${meta.color}`,
            }}
          >
            <span style={{ fontSize: 36, filter: 'drop-shadow(0 0 6px rgba(0,0,0,0.6))' }}>
              {meta.emoji}
            </span>
            <span>{meta.label}</span>
          </span>
        );
      })}
    </div>
  );
}

/** Tournament + spawn-boost HUD ribbon, top-centre of the browser
 *  source. Renders only when one or both windows are active. */
function EventBanner({
  tournament,
  spawnBoost,
}: {
  tournament: TournamentState | null;
  spawnBoost: SpawnBoostState | null;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const tActive = tournament && tournament.endsAt > now;
  const bActive = spawnBoost && spawnBoost.endsAt > now;
  if (!tActive && !bActive) return null;
  const fmt = (sec: number) => `${Math.floor(sec / 60)}m ${sec % 60}s`;
  const tSec = tActive ? Math.ceil((tournament!.endsAt - now) / 1000) : 0;
  const bSec = bActive ? Math.ceil((spawnBoost!.endsAt - now) / 1000) : 0;
  return (
    <div
      style={{
        position: 'absolute',
        top: 18,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: 10,
        zIndex: 10,
        pointerEvents: 'none',
        fontFamily: '"Orbitron",ui-sans-serif,system-ui,sans-serif',
      }}
    >
      {tActive && (
        <div
          style={{
            padding: '8px 16px',
            background: 'linear-gradient(90deg, rgba(217,70,239,0.18), rgba(217,70,239,0.32))',
            border: '1px solid rgba(217,70,239,0.55)',
            borderRadius: 999,
            color: '#fff',
            fontWeight: 800,
            fontSize: 13,
            letterSpacing: 1.5,
            textShadow: '0 2px 6px rgba(0,0,0,0.7)',
            boxShadow: '0 0 24px rgba(217,70,239,0.4)',
          }}
        >
          ⚔️ {tournament!.label ?? 'TOURNAMENT'} · {tournament!.rewardMultiplier}× ·{' '}
          <span style={{ color: '#f5d0fe' }}>{fmt(tSec)}</span>
        </div>
      )}
      {bActive && (
        <div
          style={{
            padding: '8px 16px',
            background: 'linear-gradient(90deg, rgba(251,191,36,0.18), rgba(251,191,36,0.32))',
            border: '1px solid rgba(251,191,36,0.6)',
            borderRadius: 999,
            color: '#fff',
            fontWeight: 800,
            fontSize: 13,
            letterSpacing: 1.5,
            textShadow: '0 2px 6px rgba(0,0,0,0.7)',
            boxShadow: '0 0 24px rgba(251,191,36,0.45)',
          }}
        >
          ⚡ SPAWN BOOST · <span style={{ color: '#fde68a' }}>{fmt(bSec)}</span>
        </div>
      )}
    </div>
  );
}

interface DisplayedEgg {
  egg: ActiveEgg;
  shownAt: number;
}

function EggOctagon({
  egg,
  streamerTier,
}: {
  egg: ActiveEgg | null;
  streamerTier: 'free' | 'paid';
}) {
  const [displayed, setDisplayed] = useState<DisplayedEgg | null>(null);
  const [phase, setPhase] = useState<'enter' | 'show' | 'exit'>('show');
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastEggIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (egg) {
      if (egg.id !== lastEggIdRef.current) {
        lastEggIdRef.current = egg.id;
        setDisplayed({ egg, shownAt: Date.now() });
        setPhase('enter');
        if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
        const t = setTimeout(() => setPhase('show'), ENTER_MS);
        return () => clearTimeout(t);
      }
      setDisplayed((d) => (d ? { ...d, egg } : d));
      return;
    }
    if (!displayed) return;
    lastEggIdRef.current = null;
    setPhase('exit');
    if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    exitTimerRef.current = setTimeout(() => {
      setDisplayed(null);
      setPhase('show');
    }, EXIT_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [egg]);

  useEffect(
    () => () => {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    },
    [],
  );

  if (!displayed) return null;
  const { egg: e } = displayed;
  const r = RARITY[e.rarity];
  const isReveal = e.phase === 'revealing';
  const frameColor = isReveal ? r.frame : 'rgba(255,255,255,0.45)';
  const frameGlow = isReveal ? r.glow : 'rgba(255,255,255,0.25)';
  const isPrismatic = isReveal && r.mythicPrism;
  const wrapperAnim =
    phase === 'enter'
      ? `he-popIn ${ENTER_MS}ms cubic-bezier(0.16,1,0.3,1) both`
      : phase === 'exit'
        ? `he-popOut ${EXIT_MS}ms ease-in forwards`
        : undefined;
  return (
    <div
      style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        width: OCTA_SIZE,
        height: OCTA_SIZE,
        transform: 'translate(-50%, -50%)',
        animation: wrapperAnim,
        ['--rarity-glow' as string]: frameGlow,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: isPrismatic ? r.gradient : frameColor,
          backgroundSize: isPrismatic ? '200% 100%' : undefined,
          animation: isReveal
            ? `he-octaGlow 1.6s ease-in-out infinite${isPrismatic ? ', he-octaPrism 3.5s linear infinite' : ''}`
            : undefined,
          clipPath: OCTAGON_CLIP,
          WebkitClipPath: OCTAGON_CLIP,
          transition: 'background 300ms ease, filter 200ms ease',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 6,
          background: 'radial-gradient(ellipse at 50% 0%, #1f1340 0%, #0a0420 60%, #050010 100%)',
          clipPath: OCTAGON_CLIP,
          WebkitClipPath: OCTAGON_CLIP,
          overflow: 'hidden',
        }}
      >
        <EggStage egg={e} />
        {streamerTier === 'free' && (
          <div
            style={{
              position: 'absolute',
              right: 14,
              bottom: 14,
              pointerEvents: 'none',
              zIndex: 5,
              opacity: 0.7,
              transform: 'scale(0.75)',
              transformOrigin: 'bottom right',
            }}
          >
            <Watermark variant="inline" />
          </div>
        )}
      </div>
    </div>
  );
}

function EggStage({ egg }: { egg: ActiveEgg }) {
  const prevPhaseRef = useRef<HypeEggPhase>('spawning');
  useEffect(() => {
    if (egg.phase !== prevPhaseRef.current) {
      if (egg.phase === 'spawning') void gameSounds.play('egg_spawn');
      else if (egg.phase === 'awaiting') void gameSounds.play('correct');
      else if (egg.phase === 'revealing') {
        if (egg.rarity === 'mythic' || egg.rarity === 'legendary') {
          void gameSounds.play('celebration');
        } else if (egg.rarity === 'epic') {
          void gameSounds.play('winner');
        } else {
          void gameSounds.play('reveal');
        }
      }
      prevPhaseRef.current = egg.phase;
    }
  }, [egg.phase, egg.rarity]);

  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <Header egg={egg} />
      {egg.phase === 'spawning' && <Egg shape="drop" />}
      {egg.phase === 'awaiting' && (
        <>
          <Egg shape="bob" />
          <ClaimRing egg={egg} />
        </>
      )}
      {egg.phase === 'revealing' && (
        <>
          <Egg shape="crack" />
          <RevealCard egg={egg} />
          {egg.rarity === 'mythic' && <Sparkles />}
        </>
      )}
    </div>
  );
}

function Header({ egg }: { egg: ActiveEgg }) {
  const tagline =
    egg.phase === 'spawning'
      ? 'An egg appeared!'
      : egg.phase === 'awaiting'
        ? egg.claimedBy
          ? `claimed by ${egg.claimedBy.viewer}`
          : `Type !claim — ${egg.claimCost} 💎`
        : egg.claimedBy
          ? `${egg.claimedBy.viewer} hatched it!`
          : 'No one claimed it…';
  return (
    <div
      style={{
        position: 'absolute',
        top: 28,
        left: 0,
        right: 0,
        textAlign: 'center',
        fontFamily: '"Orbitron",ui-sans-serif,system-ui,sans-serif',
        textShadow: '0 2px 6px rgba(0,0,0,0.7)',
        zIndex: 5,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          fontSize: 11,
          letterSpacing: 4,
          textTransform: 'uppercase',
          color: '#fcd34d',
          fontWeight: 700,
        }}
      >
        🥚 HypePets
      </div>
      <div
        style={{
          marginTop: 4,
          fontSize: 18,
          fontWeight: 900,
          color: '#fff',
          letterSpacing: 1.5,
        }}
      >
        {tagline}
      </div>
    </div>
  );
}

function Egg({ shape }: { shape: 'drop' | 'bob' | 'crack' }) {
  const animation =
    shape === 'drop'
      ? 'he-eggDrop 800ms cubic-bezier(0.34, 1.56, 0.64, 1) both'
      : shape === 'bob'
        ? 'he-eggBob 1.4s ease-in-out infinite'
        : 'he-eggCrack 700ms ease-in forwards';
  return (
    <>
      <div
        aria-hidden
        style={{
          position: 'absolute',
          left: '50%',
          top: '52%',
          width: 90,
          height: 120,
          transform: 'translate(-50%, 0%)',
          animation,
          zIndex: 3,
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(ellipse at 35% 30%, #fef3c7 0%, #fcd34d 35%, #b45309 95%)',
            borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
            boxShadow:
              '0 12px 28px rgba(0,0,0,0.45), inset -10px -16px 32px rgba(0,0,0,0.25), inset 8px 10px 22px rgba(255,255,255,0.35)',
          }}
        />
        {/* Spots — pure decoration */}
        <div
          style={{
            position: 'absolute',
            top: '28%',
            left: '22%',
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: 'rgba(120,53,15,0.4)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '52%',
            left: '58%',
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: 'rgba(120,53,15,0.35)',
          }}
        />
      </div>
      {shape === 'drop' && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            left: '50%',
            top: '72%',
            width: 130,
            height: 130,
            borderRadius: '50%',
            border: '3px solid #fcd34d',
            animation: 'he-splash 450ms ease-out 700ms forwards',
            transform: 'translate(-50%, -50%)',
          }}
        />
      )}
    </>
  );
}

function ClaimRing({ egg }: { egg: ActiveEgg }) {
  // Drain over the remaining window — calculate fraction once on mount
  // and animate. If the runner extends the window we'll re-mount on next
  // phase change anyway.
  const totalMs = Math.max(1000, egg.expiresAt - egg.spawnedAt);
  const remainingMs = Math.max(0, egg.expiresAt - Date.now());
  const fracElapsed = 1 - remainingMs / totalMs;
  const RADIUS = 56;
  const CIRC = 2 * Math.PI * RADIUS;
  return (
    <svg
      aria-hidden
      width={150}
      height={150}
      viewBox="0 0 150 150"
      style={{
        position: 'absolute',
        left: '50%',
        top: '72%',
        transform: 'translate(-50%, -50%) rotate(-90deg)',
        zIndex: 2,
        pointerEvents: 'none',
      }}
    >
      <circle
        cx="75"
        cy="75"
        r={RADIUS}
        fill="none"
        stroke="rgba(255,255,255,0.12)"
        strokeWidth={6}
      />
      <circle
        cx="75"
        cy="75"
        r={RADIUS}
        fill="none"
        stroke="#fbbf24"
        strokeWidth={6}
        strokeLinecap="round"
        strokeDasharray={CIRC}
        strokeDashoffset={CIRC * fracElapsed}
        style={{
          transition: `stroke-dashoffset ${remainingMs}ms linear`,
          filter: 'drop-shadow(0 0 8px rgba(251,191,36,0.6))',
        }}
      />
    </svg>
  );
}

function RevealCard({ egg }: { egg: ActiveEgg }) {
  const r = RARITY[egg.rarity];
  return (
    <div
      style={{
        position: 'absolute',
        left: '50%',
        top: '54%',
        width: 240,
        zIndex: 5,
        transform: 'translate(-50%, -50%)',
        animation: 'he-cardRise 600ms cubic-bezier(0.16,1,0.3,1) both',
      }}
    >
      <div
        style={{
          padding: 3,
          borderRadius: 16,
          background: r.gradient,
          backgroundSize: r.mythicPrism ? '200% 100%' : undefined,
          boxShadow: `0 0 28px ${r.glow}, 0 0 8px ${r.glow}`,
        }}
      >
        <div
          style={{
            borderRadius: 14,
            background: r.gradient,
            padding: '12px 14px 10px',
            color: '#fff',
            fontFamily: '"Rajdhani",ui-sans-serif,system-ui,sans-serif',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              fontSize: 9,
              letterSpacing: 4,
              fontFamily: '"Orbitron",sans-serif',
              fontWeight: 800,
              opacity: 0.9,
              textTransform: 'uppercase',
            }}
          >
            {r.label}
            {egg.claimedBy?.isShiny && <ShinyBadge inline />}
          </div>
          <CardArt
            imageUrl={egg.species.imageUrl ?? null}
            emoji={egg.species.emoji}
            shiny={!!egg.claimedBy?.isShiny}
          />
          <div
            style={{
              marginTop: 4,
              fontSize: 18,
              fontWeight: 900,
              fontFamily: '"Orbitron",sans-serif',
              letterSpacing: 1,
              lineHeight: 1.05,
            }}
          >
            {egg.species.name}
          </div>
          {egg.claimedBy && (
            <div
              style={{
                marginTop: 6,
                padding: '4px 12px',
                borderRadius: 999,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                background: 'rgba(0,0,0,0.4)',
                border: '1px solid rgba(255,255,255,0.3)',
                fontFamily: '"JetBrains Mono","Orbitron",monospace',
                fontWeight: 800,
                fontSize: 13,
              }}
            >
              hatched by {egg.claimedBy.viewer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CardArt({
  imageUrl,
  emoji,
  shiny = false,
}: {
  imageUrl: string | null;
  emoji: string;
  shiny?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [imageUrl]);
  const shinyFilter = shiny
    ? 'hue-rotate(40deg) saturate(1.3) drop-shadow(0 0 14px rgba(253,224,71,0.85))'
    : undefined;
  if (!imageUrl || failed) {
    return (
      <div
        aria-hidden
        style={{
          margin: '6px auto 0',
          width: 110,
          height: 110,
          display: 'grid',
          placeItems: 'center',
          fontSize: 64,
          lineHeight: 1,
          textShadow: shiny
            ? '0 0 18px rgba(253,224,71,0.85), 0 4px 12px rgba(0,0,0,0.5)'
            : '0 4px 12px rgba(0,0,0,0.5)',
          filter: shiny ? 'drop-shadow(0 0 8px rgba(253,224,71,0.85))' : undefined,
        }}
      >
        {emoji}
      </div>
    );
  }
  return (
    <img
      src={imageUrl}
      alt=""
      width={120}
      height={120}
      onError={() => setFailed(true)}
      style={{
        margin: '6px auto 0',
        width: 120,
        height: 120,
        objectFit: 'cover',
        borderRadius: 10,
        boxShadow: '0 6px 18px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.18)',
        filter: shinyFilter,
      }}
    />
  );
}

/** Inline gold ✨ SHINY tag used by reveal / battle / pet-display
 *  scenes when the pet has the 1%-hatch shiny flag. */
function ShinyBadge({ inline = false }: { inline?: boolean }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        marginLeft: inline ? 8 : 0,
        padding: '2px 8px',
        borderRadius: 999,
        background: 'linear-gradient(90deg, #fde047 0%, #fb923c 100%)',
        color: '#1f2937',
        fontFamily: '"Orbitron",sans-serif',
        fontWeight: 900,
        fontSize: 10,
        letterSpacing: 2,
        boxShadow: '0 0 12px rgba(253,224,71,0.7)',
      }}
    >
      ✨ SHINY
    </span>
  );
}

function Sparkles() {
  const pts = Array.from({ length: 12 }, (_, i) => ({
    left: `${10 + Math.random() * 80}%`,
    top: `${10 + Math.random() * 80}%`,
    delay: `${i * 140}ms`,
  }));
  return (
    <div aria-hidden style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 6 }}>
      {pts.map((p, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: p.left,
            top: p.top,
            width: 10,
            height: 10,
            background: 'radial-gradient(circle, #fff 0%, rgba(255,255,255,0) 70%)',
            animation: `he-sparkle 1800ms ease-in-out ${p.delay} infinite`,
          }}
        />
      ))}
    </div>
  );
}

/* ───────────────────── Pass 2 — anime tournament arena ───────────────────── */

const BATTLE_W = 1840;
const BATTLE_H = 1240;
const FIGHTER_SIZE = 392;

const BATTLE_ANIMATIONS = `
@keyframes he-arenaIn { 0% { transform: translate(-50%, -50%) scale(0.85); opacity: 0 } 100% { transform: translate(-50%, -50%) scale(1); opacity: 1 } }
@keyframes he-arenaShake { 0%, 100% { transform: translate(-50%, -50%) } 20% { transform: translate(calc(-50% - 10px), calc(-50% + 4px)) } 40% { transform: translate(calc(-50% + 12px), calc(-50% - 5px)) } 60% { transform: translate(calc(-50% - 7px), calc(-50% + 6px)) } 80% { transform: translate(calc(-50% + 6px), calc(-50% - 3px)) } }
@keyframes he-speedLines { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
@keyframes he-energyPulse { 0%, 100% { opacity: 0.55; transform: scale(1) } 50% { opacity: 0.95; transform: scale(1.08) } }
@keyframes he-vsSlamLeft { 0% { transform: translateX(-180%) skewX(-14deg); opacity: 0 } 65% { transform: translateX(8%) skewX(-6deg); opacity: 1 } 100% { transform: translateX(0) skewX(0); opacity: 1 } }
@keyframes he-vsSlamRight { 0% { transform: translateX(180%) skewX(14deg); opacity: 0 } 65% { transform: translateX(-8%) skewX(6deg); opacity: 1 } 100% { transform: translateX(0) skewX(0); opacity: 1 } }
@keyframes he-vsBurst { 0% { transform: translate(-50%, -50%) scale(0); opacity: 0 } 60% { transform: translate(-50%, -50%) scale(1.4); opacity: 1 } 80% { transform: translate(-50%, -50%) scale(0.95); opacity: 1 } 100% { transform: translate(-50%, -50%) scale(1); opacity: 1 } }
@keyframes he-vsCharIn { 0% { transform: translate(-50%, -50%) scale(2.6); opacity: 0; filter: blur(8px) } 60% { transform: translate(-50%, -50%) scale(0.92); opacity: 1; filter: blur(0) } 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; filter: blur(0) } }
@keyframes he-aIdle { 0%, 100% { transform: translateY(0) } 50% { transform: translateY(-6px) } }
@keyframes he-aLunge { 0% { transform: translate(0,0) } 30% { transform: translate(140px, -10px) scale(1.06) } 60% { transform: translate(140px, -10px) scale(1.06) } 100% { transform: translate(0,0) scale(1) } }
@keyframes he-bLunge { 0% { transform: translate(0,0) } 30% { transform: translate(-140px, -10px) scale(1.06) } 60% { transform: translate(-140px, -10px) scale(1.06) } 100% { transform: translate(0,0) scale(1) } }
@keyframes he-aRecoil { 0% { transform: translate(0,0) } 20% { transform: translate(-44px, 6px) rotate(-6deg) } 100% { transform: translate(0,0) rotate(0) } }
@keyframes he-bRecoil { 0% { transform: translate(0,0) } 20% { transform: translate(44px, 6px) rotate(6deg) } 100% { transform: translate(0,0) rotate(0) } }
@keyframes he-hitFlash { 0%, 100% { filter: brightness(1) } 15%, 45% { filter: brightness(2.5) saturate(0.4) hue-rotate(-20deg) drop-shadow(0 0 30px #ff3858) } }
@keyframes he-impactRing { 0% { transform: translate(-50%, -50%) scale(0.2); opacity: 0.9 } 100% { transform: translate(-50%, -50%) scale(3.4); opacity: 0 } }
@keyframes he-slashStripe { 0% { transform: translate(-50%, -50%) rotate(-25deg) scaleX(0); opacity: 0 } 30% { transform: translate(-50%, -50%) rotate(-25deg) scaleX(1.2); opacity: 1 } 100% { transform: translate(-50%, -50%) rotate(-25deg) scaleX(1.2); opacity: 0 } }
@keyframes he-dmgFloat { 0% { transform: translate(-50%, 0) scale(0.5); opacity: 0 } 25% { transform: translate(-50%, -20px) scale(1.15); opacity: 1 } 100% { transform: translate(-50%, -90px) scale(0.9); opacity: 0 } }
@keyframes he-finisherFlash { 0% { opacity: 0 } 12% { opacity: 1 } 60% { opacity: 0.6 } 100% { opacity: 0 } }
@keyframes he-finisherStripe { 0% { transform: translateX(-100%) skewX(-20deg); opacity: 0 } 30% { transform: translateX(0) skewX(-20deg); opacity: 1 } 100% { transform: translateX(120%) skewX(-20deg); opacity: 0 } }
@keyframes he-finisherSlam { 0% { transform: translate(-50%, -50%) scale(3.5); opacity: 0 } 40% { transform: translate(-50%, -50%) scale(0.85); opacity: 1 } 60% { transform: translate(-50%, -50%) scale(1.05); opacity: 1 } 100% { transform: translate(-50%, -50%) scale(1); opacity: 1 } }
@keyframes he-winnerRise { 0% { transform: translate(-50%, 30px) scale(0.85); opacity: 0 } 100% { transform: translate(-50%, 0) scale(1); opacity: 1 } }
@keyframes he-winnerPose { 0%, 100% { transform: translateY(0) } 50% { transform: translateY(-10px) } }
@keyframes he-winnerHalo { 0%, 100% { opacity: 0.5; transform: translate(-50%, -50%) scale(1) } 50% { opacity: 1; transform: translate(-50%, -50%) scale(1.08) } }
@keyframes he-loserSlump { 0% { transform: translate(0,0) rotate(0); opacity: 1; filter: saturate(1) } 100% { transform: translate(-12px, 18px) rotate(-14deg); opacity: 0.4; filter: saturate(0.2) brightness(0.6) } }
@keyframes he-koStamp { 0% { transform: translate(-50%, -50%) scale(4) rotate(-30deg); opacity: 0 } 35% { transform: translate(-50%, -50%) scale(0.9) rotate(-12deg); opacity: 1 } 60% { transform: translate(-50%, -50%) scale(1.1) rotate(-12deg); opacity: 1 } 100% { transform: translate(-50%, -50%) scale(1) rotate(-12deg); opacity: 1 } }
@keyframes he-winsBanner { 0% { transform: translate(-50%, 40px) scale(0.5); opacity: 0 } 55% { transform: translate(-50%, 0) scale(1.15); opacity: 1 } 100% { transform: translate(-50%, 0) scale(1); opacity: 1 } }
@keyframes he-xpPulse { 0% { transform: translateX(-50%) scale(0.6); opacity: 0 } 60% { transform: translateX(-50%) scale(1.15); opacity: 1 } 100% { transform: translateX(-50%) scale(1); opacity: 1 } }
@keyframes he-particleRise { 0% { transform: translateY(40px); opacity: 0 } 20% { opacity: 1 } 100% { transform: translateY(-360px); opacity: 0 } }
`;

function BattleArena({
  battle,
  streamerTier,
}: {
  battle: ActiveBattle;
  streamerTier: 'free' | 'paid';
}) {
  const total = battle.moves.length;
  const idx = battle.currentMoveIndex;
  const isIntro = battle.phase === 'intro';
  const isFighting = battle.phase === 'fighting';
  const isOutcome = battle.phase === 'outcome';
  const winnerSide = battle.winnerSide;
  const winnerRarity = winnerSide === 'a' ? battle.a.rarity : battle.b.rarity;
  // The arena tint while fighting follows whichever pet just struck (a
  // visual rhythm that telegraphs momentum); during outcome it locks
  // to the winner's rarity for the wins/loses-the-tournament feel.
  const lastMove: BattleMove | null = idx >= 0 && idx < total ? (battle.moves[idx] ?? null) : null;
  const tintRarity: HypeEggRarity = isOutcome
    ? winnerRarity
    : lastMove
      ? lastMove.attacker === 'a'
        ? battle.a.rarity
        : battle.b.rarity
      : 'common';
  const tint = RARITY[tintRarity];
  const isFinisher = isFighting && lastMove?.ko === true && idx === total - 1;

  // Walk the moves up through idx to derive each side's current HP.
  let hpA = battle.a.stats.hp;
  let hpB = battle.b.stats.hp;
  for (let i = 0; i <= Math.min(idx, total - 1); i++) {
    const m = battle.moves[i];
    if (!m) break;
    if (m.attacker === 'a') hpB = m.defenderHp;
    else hpA = m.defenderHp;
  }

  return (
    <div
      style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        width: BATTLE_W,
        height: BATTLE_H,
        transform: 'translate(-50%, -50%)',
        animation: isFinisher
          ? `he-arenaShake 380ms ease 1, he-arenaIn ${ENTER_MS}ms cubic-bezier(0.16,1,0.3,1) both`
          : lastMove
            ? `he-arenaShake 260ms ease 1, he-arenaIn ${ENTER_MS}ms cubic-bezier(0.16,1,0.3,1) both`
            : `he-arenaIn ${ENTER_MS}ms cubic-bezier(0.16,1,0.3,1) both`,
        ['--rarity-glow' as string]: tint.glow,
        pointerEvents: 'none',
      }}
      key={`arena-${idx}`}
    >
      <style>{BATTLE_ANIMATIONS}</style>
      {/* outer frame: rounded rectangle, glow tinted by tint rarity */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: 28,
          padding: 4,
          background: tint.mythicPrism && isOutcome ? tint.gradient : tint.frame,
          backgroundSize: tint.mythicPrism && isOutcome ? '200% 100%' : undefined,
          animation: `he-octaGlow 1.6s ease-in-out infinite${tint.mythicPrism && isOutcome ? ', he-octaPrism 3.5s linear infinite' : ''}`,
        }}
      />
      {/* inner stage */}
      <div
        style={{
          position: 'absolute',
          inset: 4,
          borderRadius: 24,
          background: 'radial-gradient(ellipse at 50% 40%, #1f0b3a 0%, #0a0316 65%, #02000a 100%)',
          overflow: 'hidden',
        }}
      >
        <ArenaBackground
          tintRarity={tintRarity}
          intensity={isFinisher ? 'finisher' : isOutcome ? 'outcome' : 'fight'}
        />
        <ArenaHeader battle={battle} idx={idx} total={total} />

        {isIntro ? (
          <IntroVS battle={battle} />
        ) : isOutcome ? (
          <OutcomeScene battle={battle} hpA={hpA} hpB={hpB} />
        ) : (
          <FightingScene
            battle={battle}
            idx={idx}
            hpA={hpA}
            hpB={hpB}
            lastMove={lastMove}
            isFinisher={isFinisher}
          />
        )}

        {streamerTier === 'free' && (
          <div
            style={{
              position: 'absolute',
              right: 18,
              bottom: 16,
              pointerEvents: 'none',
              zIndex: 9,
              opacity: 0.7,
              transform: 'scale(0.78)',
              transformOrigin: 'bottom right',
            }}
          >
            <Watermark variant="inline" />
          </div>
        )}
      </div>
    </div>
  );
}

/** Animated background — radial energy pulse + rotating speed lines,
 *  tinted by the currently-attacking pet's rarity. Intensity ramps
 *  during finisher / outcome to make the arena feel more charged. */
function ArenaBackground({
  tintRarity,
  intensity,
}: {
  tintRarity: HypeEggRarity;
  intensity: 'fight' | 'finisher' | 'outcome';
}) {
  const tint = RARITY[tintRarity];
  const pulseColor = tint.glow;
  const linesOpacity = intensity === 'finisher' ? 0.55 : intensity === 'outcome' ? 0.4 : 0.22;
  const linesSpeed = intensity === 'finisher' ? '6s' : intensity === 'outcome' ? '14s' : '24s';
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
      {/* radial energy pulse */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '52%',
          width: 1200,
          height: 1200,
          transform: 'translate(-50%, -50%)',
          background: `radial-gradient(circle, ${pulseColor} 0%, transparent 55%)`,
          mixBlendMode: 'screen',
          animation: 'he-energyPulse 2.2s ease-in-out infinite',
        }}
      />
      {/* rotating speed lines via repeating conic gradient */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          width: 1600,
          height: 1600,
          transform: 'translate(-50%, -50%)',
          background:
            'repeating-conic-gradient(from 0deg, rgba(255,255,255,0.55) 0deg 0.6deg, transparent 0.6deg 4deg)',
          opacity: linesOpacity,
          animation: `he-speedLines ${linesSpeed} linear infinite`,
          mixBlendMode: 'screen',
        }}
      />
      {/* vignette */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(ellipse at 50% 55%, transparent 35%, rgba(0,0,0,0.7) 100%)',
        }}
      />
    </div>
  );
}

function ArenaHeader({ battle, idx, total }: { battle: ActiveBattle; idx: number; total: number }) {
  const subline =
    battle.phase === 'intro'
      ? 'Get ready…'
      : battle.phase === 'fighting'
        ? `Turn ${Math.min(idx + 1, total)} / ${total}`
        : `${battle.winnerSide === 'a' ? battle.a.viewer : battle.b.viewer} wins!`;
  return (
    <div
      style={{
        position: 'absolute',
        top: 22,
        left: 0,
        right: 0,
        textAlign: 'center',
        fontFamily: '"Orbitron",ui-sans-serif,system-ui,sans-serif',
        textShadow: '0 2px 8px rgba(0,0,0,0.85)',
        zIndex: 6,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          fontSize: 12,
          letterSpacing: 5,
          textTransform: 'uppercase',
          color: '#fda4af',
          fontWeight: 800,
        }}
      >
        {battle.isWild ? '🌿 Wild Encounter' : '⚔️ HypePets Battle'}
      </div>
      <div
        style={{
          marginTop: 4,
          fontSize: 22,
          fontWeight: 900,
          color: '#fff',
          letterSpacing: 2,
        }}
      >
        {battle.a.viewer} <span style={{ color: '#fda4af' }}>vs</span> {battle.b.viewer}
      </div>
      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 4 }}>{subline}</div>
    </div>
  );
}

/** Intro beat — both fighters slam in from opposite sides of the
 *  arena, freeze, and a huge "VS" punches in between them. */
function IntroVS({ battle }: { battle: ActiveBattle }) {
  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 4 }}>
      <div
        style={{
          position: 'absolute',
          left: '6%',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '38%',
          animation: 'he-vsSlamLeft 600ms cubic-bezier(0.16,1,0.3,1) both',
        }}
      >
        <FighterPortrait side={battle.a} align="left" big />
      </div>
      <div
        style={{
          position: 'absolute',
          right: '6%',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '38%',
          animation: 'he-vsSlamRight 600ms cubic-bezier(0.16,1,0.3,1) both',
        }}
      >
        <FighterPortrait side={battle.b} align="right" big />
      </div>
      {/* "VS" emblem */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 180,
          height: 180,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(253,164,175,0.45) 0%, rgba(253,164,175,0.15) 50%, transparent 75%)',
          animation: 'he-vsBurst 500ms cubic-bezier(0.16,1,0.3,1) 400ms both',
          mixBlendMode: 'screen',
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          fontFamily: '"Orbitron",sans-serif',
          fontWeight: 900,
          fontSize: 120,
          letterSpacing: -4,
          color: '#fff',
          textShadow: '0 0 22px rgba(253,164,175,0.95), 0 0 6px #fff, 4px 6px 0 rgba(0,0,0,0.5)',
          WebkitTextStroke: '3px #fda4af',
          animation: 'he-vsCharIn 600ms cubic-bezier(0.16,1,0.3,1) 350ms both',
          pointerEvents: 'none',
        }}
      >
        VS
      </div>
    </div>
  );
}

/** Fighting beat — sprites stand at their corners, attacker lunges on
 *  each move tick, defender takes a hit (flash + recoil + dmg float). */
function FightingScene({
  battle,
  idx,
  hpA,
  hpB,
  lastMove,
  isFinisher,
}: {
  battle: ActiveBattle;
  idx: number;
  hpA: number;
  hpB: number;
  lastMove: BattleMove | null;
  isFinisher: boolean;
}) {
  const attackerSide = lastMove?.attacker ?? null;
  const aAttacking = attackerSide === 'a';
  const bAttacking = attackerSide === 'b';
  // Pass 28i — dodged strikes don't recoil the defender; only the
  // attacker still plays the lunge.
  const wasDodged = lastMove?.dodged === true;
  const aStruck = attackerSide === 'b' && !wasDodged;
  const bStruck = attackerSide === 'a' && !wasDodged;
  const dmg = lastMove?.damage ?? 0;
  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 3 }}>
      <FighterStage
        side={battle.a}
        currentHp={hpA}
        attacking={aAttacking}
        struck={aStruck}
        align="left"
        moveKey={idx}
      />
      <FighterStage
        side={battle.b}
        currentHp={hpB}
        attacking={bAttacking}
        struck={bStruck}
        align="right"
        moveKey={idx}
      />
      {/* impact FX over the defender. Pass 28i — on a SPD-dodge the
          slash/ring suppress and a "DODGE" stamp renders instead. */}
      {lastMove && (
        <ImpactFX
          key={`impact-${idx}`}
          side={lastMove.attacker === 'a' ? 'right' : 'left'}
          damage={dmg}
          dodged={lastMove.dodged === true}
        />
      )}
      {isFinisher && (
        <FinisherOverlay attacker={lastMove!.attacker === 'a' ? battle.a : battle.b} />
      )}
    </div>
  );
}

/** Per-fighter render in the fighting scene. Sprite + name + HP bar.
 *  `moveKey` is the move index — using it as React key restarts the
 *  CSS animation each turn so the lunge/recoil plays cleanly. */
function FighterStage({
  side,
  currentHp,
  attacking,
  struck,
  align,
  moveKey,
}: {
  side: BattleSide;
  currentHp: number;
  attacking: boolean;
  struck: boolean;
  align: 'left' | 'right';
  moveKey: number;
}) {
  const maxHp = side.stats.hp;
  const pct = Math.max(0, Math.min(100, (currentHp / maxHp) * 100));
  const hpColor = pct > 60 ? '#22c55e' : pct > 30 ? '#fbbf24' : '#ef4444';
  const r = RARITY[side.rarity];
  const left = align === 'left' ? '7%' : 'auto';
  const right = align === 'right' ? '7%' : 'auto';
  const lungeAnim = align === 'left' ? 'he-aLunge 700ms ease 1' : 'he-bLunge 700ms ease 1';
  const recoilAnim = align === 'left' ? 'he-aRecoil 360ms ease 1' : 'he-bRecoil 360ms ease 1';
  const spriteAnim = attacking
    ? lungeAnim
    : struck
      ? recoilAnim
      : 'he-aIdle 2.6s ease-in-out infinite';
  const spriteFilter = struck ? 'he-hitFlash 360ms ease 1' : undefined;
  return (
    <div
      style={{
        position: 'absolute',
        left,
        right,
        top: '38%',
        width: 320,
        textAlign: 'center',
        zIndex: 3,
      }}
    >
      {/* sprite */}
      <div
        key={`sprite-${align}-${moveKey}-${attacking ? 'a' : struck ? 's' : 'i'}`}
        style={{
          margin: '0 auto',
          width: FIGHTER_SIZE,
          height: FIGHTER_SIZE,
          animation: spriteAnim,
        }}
      >
        <div
          key={`flash-${align}-${moveKey}`}
          style={{
            width: '100%',
            height: '100%',
            animation: spriteFilter,
          }}
        >
          {side.imageUrl ? (
            <img
              src={side.imageUrl}
              alt=""
              width={FIGHTER_SIZE}
              height={FIGHTER_SIZE}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                filter: `drop-shadow(0 0 22px ${side.isShiny ? 'rgba(253,224,71,0.85)' : r.glow}) drop-shadow(0 6px 12px rgba(0,0,0,0.7))`,
              }}
            />
          ) : (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'grid',
                placeItems: 'center',
                fontSize: 120,
                filter: `drop-shadow(0 0 22px ${r.glow}) drop-shadow(0 6px 12px rgba(0,0,0,0.7))`,
              }}
            >
              {side.emoji}
            </div>
          )}
        </div>
      </div>
      {/* name + level pill below sprite */}
      <div
        style={{
          marginTop: 8,
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '4px 14px',
          borderRadius: 999,
          background: 'rgba(0,0,0,0.55)',
          border: `1px solid ${r.frame}`,
        }}
      >
        <div
          style={{
            fontFamily: '"Orbitron",sans-serif',
            fontSize: 11,
            color: r.frame,
            fontWeight: 700,
            letterSpacing: 2,
            textTransform: 'uppercase',
          }}
        >
          Lv {side.level}
          {side.isShiny && <ShinyBadge inline />}
        </div>
        <div
          style={{
            fontFamily: '"Orbitron",sans-serif',
            fontSize: 15,
            color: '#fff',
            fontWeight: 800,
          }}
        >
          {side.nickname ?? side.speciesName}
        </div>
      </div>
      {/* HP bar */}
      <div
        style={{
          margin: '10px auto 0',
          width: 240,
          height: 14,
          background: 'rgba(0,0,0,0.7)',
          border: '1px solid rgba(255,255,255,0.25)',
          borderRadius: 8,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            background: hpColor,
            transition: 'width 600ms cubic-bezier(0.16,1,0.3,1), background 300ms ease',
          }}
        />
      </div>
      <div
        style={{
          marginTop: 4,
          fontFamily: '"JetBrains Mono",monospace',
          fontSize: 12,
          color: '#fff',
          textShadow: '0 1px 3px rgba(0,0,0,0.9)',
        }}
      >
        {Math.max(0, currentHp)} / {maxHp} HP
      </div>
    </div>
  );
}

/** Impact FX — slash stripe + radial ring + floating damage number
 *  over whichever side just got hit. Mounts fresh every move via the
 *  parent's `key={`impact-${idx}`}` so animations restart cleanly. */
function ImpactFX({
  side,
  damage,
  dodged,
}: {
  side: 'left' | 'right';
  damage: number;
  dodged?: boolean;
}) {
  const x = side === 'left' ? '23%' : '77%';
  if (dodged) {
    return (
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 5 }}>
        <div
          style={{
            position: 'absolute',
            left: x,
            top: '46%',
            transform: 'translate(-50%, 0)',
            fontFamily: '"Orbitron",sans-serif',
            fontWeight: 900,
            fontSize: 52,
            color: '#38bdf8',
            letterSpacing: 4,
            textShadow: '0 0 14px rgba(0,0,0,0.85), 0 2px 0 #0369a1, 0 0 24px rgba(56,189,248,0.6)',
            WebkitTextStroke: '2px #0369a1',
            animation: 'he-dmgFloat 900ms cubic-bezier(0.2,0.7,0.3,1) 1',
          }}
        >
          💨 DODGE
        </div>
      </div>
    );
  }
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 5 }}>
      <div
        style={{
          position: 'absolute',
          left: x,
          top: '54%',
          width: 220,
          height: 8,
          background: 'linear-gradient(90deg, transparent 0%, #fff 50%, transparent 100%)',
          boxShadow: '0 0 18px #fff, 0 0 36px rgba(253,164,175,0.9)',
          animation: 'he-slashStripe 320ms ease 1',
          transformOrigin: 'center',
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: x,
          top: '54%',
          width: 80,
          height: 80,
          borderRadius: '50%',
          border: '6px solid rgba(255,255,255,0.95)',
          boxShadow: '0 0 24px rgba(253,164,175,0.85)',
          animation: 'he-impactRing 520ms ease-out 1',
        }}
      />
      {damage > 0 && (
        <div
          style={{
            position: 'absolute',
            left: x,
            top: '46%',
            transform: 'translate(-50%, 0)',
            fontFamily: '"Orbitron",sans-serif',
            fontWeight: 900,
            fontSize: damage > 50 ? 56 : 44,
            color: damage > 50 ? '#fde047' : '#fff',
            textShadow: '0 0 14px rgba(0,0,0,0.85), 0 2px 0 #b91c1c, 0 4px 0 rgba(0,0,0,0.6)',
            WebkitTextStroke: '2px #b91c1c',
            animation: 'he-dmgFloat 900ms cubic-bezier(0.2,0.7,0.3,1) 1',
          }}
        >
          -{damage}
        </div>
      )}
    </div>
  );
}

/** Finisher overlay — fires only on the killing move. Full-arena
 *  white flash + a diagonal stripe sweep + the attacker's name slammed
 *  center as a "FINISHING BLOW" callout. */
function FinisherOverlay({ attacker }: { attacker: BattleSide }) {
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 7 }}>
      {/* full-arena white flash */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: '#fff',
          mixBlendMode: 'screen',
          animation: 'he-finisherFlash 520ms ease 1',
        }}
      />
      {/* diagonal stripe sweep */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: '30%',
          width: '100%',
          height: '40%',
          background:
            'linear-gradient(90deg, transparent 0%, rgba(253,164,175,0.85) 40%, #fff 50%, rgba(253,164,175,0.85) 60%, transparent 100%)',
          animation: 'he-finisherStripe 700ms ease-out 1',
          mixBlendMode: 'screen',
        }}
      />
      {/* center text slam */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '52%',
          textAlign: 'center',
          animation: 'he-finisherSlam 700ms cubic-bezier(0.16,1,0.3,1) 1',
        }}
      >
        <div
          style={{
            fontFamily: '"Orbitron",sans-serif',
            fontWeight: 900,
            fontSize: 36,
            letterSpacing: 6,
            color: '#fda4af',
            textShadow: '0 0 18px rgba(0,0,0,0.9)',
            textTransform: 'uppercase',
          }}
        >
          Finishing Blow
        </div>
        <div
          style={{
            marginTop: 4,
            fontFamily: '"Orbitron",sans-serif',
            fontWeight: 900,
            fontSize: 46,
            color: '#fff',
            textShadow: '0 0 22px rgba(253,164,175,0.95), 0 4px 0 rgba(0,0,0,0.65)',
            WebkitTextStroke: '2px #fda4af',
          }}
        >
          {attacker.nickname ?? attacker.speciesName}
        </div>
      </div>
    </div>
  );
}

/** Outcome beat — winner centered, loser slumped on the side, "WINS!"
 *  banner, XP/gems pill, optional level-up flash, particles rising. */
function OutcomeScene({ battle, hpA, hpB }: { battle: ActiveBattle; hpA: number; hpB: number }) {
  const winnerKey = battle.winnerSide;
  const winner = winnerKey === 'a' ? battle.a : battle.b;
  const loser = winnerKey === 'a' ? battle.b : battle.a;
  const winnerHp = winnerKey === 'a' ? hpA : hpB;
  const wR = RARITY[winner.rarity];
  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 4 }}>
      {/* loser slumped on the side */}
      <div
        style={{
          position: 'absolute',
          left: winnerKey === 'a' ? 'auto' : '8%',
          right: winnerKey === 'a' ? '8%' : 'auto',
          top: '46%',
          width: 160,
          textAlign: 'center',
          animation: 'he-loserSlump 700ms cubic-bezier(0.4,0,0.6,1) both',
        }}
      >
        <div style={{ position: 'relative', width: 160, height: 160, margin: '0 auto' }}>
          {loser.imageUrl ? (
            <img
              src={loser.imageUrl}
              alt=""
              width={160}
              height={160}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.7))',
              }}
            />
          ) : (
            <div style={{ fontSize: 110, lineHeight: 1 }}>{loser.emoji}</div>
          )}
          {/* KO stamp */}
          <div
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              fontFamily: '"Orbitron",sans-serif',
              fontWeight: 900,
              fontSize: 64,
              letterSpacing: 4,
              color: '#fff',
              textShadow: '0 0 16px rgba(0,0,0,0.85)',
              WebkitTextStroke: '4px #dc2626',
              padding: '0 12px',
              border: '5px solid #dc2626',
              borderRadius: 8,
              background: 'rgba(0,0,0,0.6)',
              animation: 'he-koStamp 600ms cubic-bezier(0.16,1,0.3,1) 200ms both',
            }}
          >
            KO
          </div>
        </div>
        <div
          style={{
            marginTop: 6,
            fontFamily: '"Orbitron",sans-serif',
            fontSize: 12,
            color: 'rgba(255,255,255,0.6)',
            fontWeight: 700,
            letterSpacing: 2,
            textTransform: 'uppercase',
          }}
        >
          {loser.nickname ?? loser.speciesName}
        </div>
      </div>

      {/* winner centerpiece */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: 110,
          transform: 'translateX(-50%)',
          width: 360,
          textAlign: 'center',
          animation: 'he-winnerRise 500ms cubic-bezier(0.16,1,0.3,1) both',
        }}
      >
        {/* winner halo */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '38%',
            width: 320,
            height: 320,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${wR.glow} 0%, transparent 70%)`,
            transform: 'translate(-50%, -50%)',
            animation: 'he-winnerHalo 1.8s ease-in-out infinite',
            mixBlendMode: 'screen',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'relative',
            margin: '0 auto',
            width: 240,
            height: 240,
            animation: 'he-winnerPose 1.6s ease-in-out infinite',
          }}
        >
          {winner.imageUrl ? (
            <img
              src={winner.imageUrl}
              alt=""
              width={240}
              height={240}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                filter: `drop-shadow(0 0 28px ${winner.isShiny ? 'rgba(253,224,71,0.95)' : wR.glow}) drop-shadow(0 10px 14px rgba(0,0,0,0.7))`,
              }}
            />
          ) : (
            <div style={{ fontSize: 180, lineHeight: 1 }}>{winner.emoji}</div>
          )}
        </div>
        <div
          style={{
            marginTop: 10,
            fontFamily: '"Orbitron",sans-serif',
            fontSize: 11,
            color: wR.frame,
            fontWeight: 700,
            letterSpacing: 3,
            textTransform: 'uppercase',
          }}
        >
          Lv {winner.level} · {Math.max(0, winnerHp)} HP
          {winner.isShiny && <ShinyBadge inline />}
        </div>
        <div
          style={{
            fontFamily: '"Orbitron",sans-serif',
            fontSize: 22,
            color: '#fff',
            fontWeight: 800,
          }}
        >
          {winner.nickname ?? winner.speciesName}
        </div>
        {/* WINS! banner */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            bottom: -50,
            transform: 'translateX(-50%)',
            padding: '6px 28px',
            borderRadius: 8,
            background: wR.mythicPrism ? wR.gradient : wR.frame,
            backgroundSize: wR.mythicPrism ? '200% 100%' : undefined,
            animation: `he-winsBanner 600ms cubic-bezier(0.16,1,0.3,1) 250ms both${wR.mythicPrism ? ', he-octaPrism 3.5s linear infinite' : ''}`,
            fontFamily: '"Orbitron",sans-serif',
            fontWeight: 900,
            fontSize: 38,
            letterSpacing: 6,
            color: '#fff',
            textShadow: '0 2px 6px rgba(0,0,0,0.7)',
            WebkitTextStroke: '2px rgba(0,0,0,0.4)',
            whiteSpace: 'nowrap',
          }}
        >
          WINS!
        </div>
      </div>

      {/* XP / gems pill + optional level-up flash, bottom-center */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          bottom: 38,
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          zIndex: 6,
          animation: 'he-xpPulse 500ms cubic-bezier(0.16,1,0.3,1) 700ms both',
        }}
      >
        <div
          style={{
            padding: '8px 18px',
            borderRadius: 999,
            background: 'rgba(0,0,0,0.6)',
            border: '1px solid rgba(255,255,255,0.35)',
            fontFamily: '"JetBrains Mono","Orbitron",monospace',
            fontWeight: 800,
            color: '#fff',
            fontSize: 16,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 14,
          }}
        >
          <span style={{ color: '#a5f3fc' }}>+{battle.xpWinner} XP</span>
          <span style={{ color: '#86efac' }}>+{battle.gemsAwarded} 💎</span>
        </div>
        {battle.winnerLevelUp && (
          <div
            style={{
              fontFamily: '"Orbitron",sans-serif',
              fontWeight: 900,
              fontSize: 22,
              color: '#fde047',
              textShadow: '0 0 16px rgba(253,224,71,0.95)',
            }}
          >
            ⭐ LEVEL UP → Lv {battle.winnerLevelUp}
          </div>
        )}
      </div>

      {/* particles rising from the winner */}
      <ParticleField rarity={winner.rarity} />
    </div>
  );
}

/** Decorative particle field rising from the winner — drifts up over
 *  the outcome hold to sell the celebration. */
function ParticleField({ rarity }: { rarity: HypeEggRarity }) {
  const color = RARITY[rarity].glow;
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 2 }}>
      {Array.from({ length: 18 }, (_, i) => {
        const left = 30 + Math.random() * 40;
        const delay = Math.random() * 1.6;
        const dur = 1.8 + Math.random() * 1.6;
        const size = 4 + Math.random() * 8;
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: `${left}%`,
              bottom: 40,
              width: size,
              height: size,
              borderRadius: '50%',
              background: color,
              boxShadow: `0 0 ${size * 2}px ${color}`,
              animation: `he-particleRise ${dur}s ease-out ${delay}s infinite`,
              opacity: 0,
              ['--i' as string]: i,
            }}
          />
        );
      })}
    </div>
  );
}

/** Compact name/level/sprite portrait used by the intro VS beat. */
function FighterPortrait({
  side,
  align,
  big = false,
}: {
  side: BattleSide;
  align: 'left' | 'right';
  big?: boolean;
}) {
  const r = RARITY[side.rarity];
  const spriteSize = big ? 200 : 120;
  return (
    <div style={{ textAlign: align === 'left' ? 'left' : 'right' }}>
      <div
        style={{
          width: spriteSize,
          height: spriteSize,
          margin: align === 'left' ? '0 auto 0 0' : '0 0 0 auto',
          animation: 'he-aIdle 2.6s ease-in-out infinite',
        }}
      >
        {side.imageUrl ? (
          <img
            src={side.imageUrl}
            alt=""
            width={spriteSize}
            height={spriteSize}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              filter: `drop-shadow(0 0 24px ${side.isShiny ? 'rgba(253,224,71,0.85)' : r.glow}) drop-shadow(0 6px 12px rgba(0,0,0,0.7))`,
            }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'grid',
              placeItems: 'center',
              fontSize: spriteSize * 0.7,
              filter: `drop-shadow(0 0 24px ${r.glow})`,
            }}
          >
            {side.emoji}
          </div>
        )}
      </div>
      <div
        style={{
          marginTop: 10,
          fontFamily: '"Orbitron",sans-serif',
          fontWeight: 800,
          fontSize: 20,
          color: '#fff',
          textShadow: '0 2px 6px rgba(0,0,0,0.8)',
        }}
      >
        {side.viewer}
      </div>
      <div
        style={{
          fontFamily: '"Orbitron",sans-serif',
          fontSize: 13,
          color: r.frame,
          fontWeight: 700,
          letterSpacing: 2,
          textTransform: 'uppercase',
          textShadow: '0 2px 4px rgba(0,0,0,0.7)',
        }}
      >
        Lv {side.level} · {side.nickname ?? side.speciesName}
      </div>
    </div>
  );
}

/* ───────────────────── Pass 3 — !pet display ───────────────────── */

function PetDisplayOctagon({
  display,
  streamerTier,
}: {
  display: ActivePetDisplay;
  streamerTier: 'free' | 'paid';
}) {
  const r = RARITY[display.rarity];
  const isPrismatic = r.mythicPrism;
  return (
    <div
      style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        width: OCTA_SIZE,
        height: OCTA_SIZE,
        transform: 'translate(-50%, -50%)',
        animation: `he-popIn ${ENTER_MS}ms cubic-bezier(0.16,1,0.3,1) both`,
        ['--rarity-glow' as string]: r.glow,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: isPrismatic ? r.gradient : r.frame,
          backgroundSize: isPrismatic ? '200% 100%' : undefined,
          animation: `he-octaGlow 1.6s ease-in-out infinite${isPrismatic ? ', he-octaPrism 3.5s linear infinite' : ''}`,
          clipPath: OCTAGON_CLIP,
          WebkitClipPath: OCTAGON_CLIP,
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 6,
          background: 'radial-gradient(ellipse at 50% 0%, #1a0e2e 0%, #060212 60%, #02000a 100%)',
          clipPath: OCTAGON_CLIP,
          WebkitClipPath: OCTAGON_CLIP,
          overflow: 'hidden',
        }}
      >
        <PetDisplayStage display={display} />
        {streamerTier === 'free' && (
          <div
            style={{
              position: 'absolute',
              right: 14,
              bottom: 14,
              pointerEvents: 'none',
              zIndex: 5,
              opacity: 0.7,
              transform: 'scale(0.75)',
              transformOrigin: 'bottom right',
            }}
          >
            <Watermark variant="inline" />
          </div>
        )}
      </div>
    </div>
  );
}

function PetDisplayStage({ display }: { display: ActivePetDisplay }) {
  const r = RARITY[display.rarity];
  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 0,
          right: 0,
          textAlign: 'center',
          fontFamily: '"Orbitron",ui-sans-serif,system-ui,sans-serif',
          textShadow: '0 2px 6px rgba(0,0,0,0.7)',
          zIndex: 5,
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            fontSize: 11,
            letterSpacing: 4,
            textTransform: 'uppercase',
            color: r.frame,
            fontWeight: 700,
          }}
        >
          🐾 Active Pet
        </div>
        <div
          style={{
            marginTop: 4,
            fontSize: 18,
            fontWeight: 900,
            color: '#fff',
            letterSpacing: 1.5,
          }}
        >
          {display.viewer}
        </div>
      </div>
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          width: 280,
        }}
      >
        <div
          style={{
            fontFamily: '"Orbitron",sans-serif',
            fontSize: 10,
            color: r.frame,
            fontWeight: 700,
            letterSpacing: 3,
            textTransform: 'uppercase',
          }}
        >
          {r.label}
          {display.isShiny && <ShinyBadge inline />}
        </div>
        {display.imageUrl ? (
          <img
            src={display.imageUrl}
            alt=""
            width={150}
            height={150}
            style={{
              margin: '8px auto 0',
              width: 150,
              height: 150,
              objectFit: 'cover',
              borderRadius: 14,
              border: `3px solid ${r.frame}`,
              boxShadow: `0 0 28px ${r.glow}, inset 0 1px 0 rgba(255,255,255,0.15)`,
            }}
          />
        ) : (
          <div
            style={{
              margin: '8px auto 0',
              width: 150,
              height: 150,
              display: 'grid',
              placeItems: 'center',
              fontSize: 80,
              borderRadius: 14,
              border: `3px solid ${r.frame}`,
              boxShadow: `0 0 28px ${r.glow}`,
              background: 'rgba(0,0,0,0.4)',
            }}
          >
            {display.emoji}
          </div>
        )}
        <div
          style={{
            marginTop: 12,
            fontSize: 22,
            fontWeight: 900,
            fontFamily: '"Orbitron",sans-serif',
            letterSpacing: 1,
            color: '#fff',
          }}
        >
          {display.nickname ?? display.speciesName}
        </div>
        {display.nickname && (
          <div
            style={{
              fontSize: 11,
              color: 'rgba(255,255,255,0.6)',
              fontFamily: '"Rajdhani",sans-serif',
              fontStyle: 'italic',
            }}
          >
            {display.speciesName}
          </div>
        )}
        <div
          style={{
            marginTop: 8,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            padding: '6px 14px',
            borderRadius: 999,
            background: 'rgba(0,0,0,0.5)',
            border: '1px solid rgba(255,255,255,0.25)',
            fontFamily: '"JetBrains Mono","Orbitron",monospace',
            fontWeight: 800,
            color: '#fff',
            fontSize: 14,
          }}
        >
          <span style={{ color: '#fde047' }}>Lv {display.level}</span>
          <span style={{ color: 'rgba(255,255,255,0.4)' }}>·</span>
          <span style={{ color: '#86efac' }}>{display.wins}W</span>
          <span style={{ color: '#fca5a5' }}>{display.losses}L</span>
          <span style={{ color: 'rgba(255,255,255,0.4)' }}>·</span>
          <span style={{ color: '#a5f3fc' }}>{display.xp} XP</span>
        </div>
        {/* Pass 5 — HP bar + faint badge. Only renders if HP fields
            are present on the snapshot (older state writes omit them). */}
        {typeof display.currentHp === 'number' &&
          typeof display.maxHp === 'number' &&
          display.maxHp > 0 && (
            <PetHpBar
              currentHp={display.currentHp}
              maxHp={display.maxHp}
              fainted={!!display.isFainted}
            />
          )}
      </div>
    </div>
  );
}

function PetHpBar({
  currentHp,
  maxHp,
  fainted,
}: {
  currentHp: number;
  maxHp: number;
  fainted: boolean;
}) {
  const pct = Math.max(0, Math.min(100, (currentHp / Math.max(1, maxHp)) * 100));
  const color = fainted ? '#ef4444' : pct > 60 ? '#22c55e' : pct > 30 ? '#fbbf24' : '#ef4444';
  return (
    <div style={{ marginTop: 10, width: 200, marginInline: 'auto' }}>
      <div
        style={{
          height: 12,
          background: 'rgba(0,0,0,0.55)',
          border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: 6,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            background: color,
            transition: 'width 400ms ease, background 200ms ease',
          }}
        />
      </div>
      <div
        style={{
          marginTop: 4,
          fontFamily: '"JetBrains Mono",monospace',
          fontSize: 11,
          color: fainted ? '#fca5a5' : '#fff',
          fontWeight: 700,
        }}
      >
        {fainted ? 'FAINTED' : `${currentHp} / ${maxHp} HP`}
      </div>
    </div>
  );
}
